import React, { useEffect, useRef } from 'react';
import { WizardStepper } from './WizardStepper';
import { WizardFooter } from './WizardFooter';
import type { WizardFooterProps } from './WizardFooter';

interface Step {
  number: number;
  label: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

type WizardLayoutFooterProps = Omit<WizardFooterProps, 'fixedToViewport' | 'leftOffset' | 'rightOffsetPx'>;

interface WizardLayoutProps extends WizardLayoutFooterProps {
  // Stepper
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick?: (step: number) => void;
  allowNavigation?: boolean;

  // Container
  className?: string;
  children: React.ReactNode;

  /** Extra right margin on the main container (e.g. for a side panel) */
  rightMargin?: number;

  /** Mode embarqué : stepper/footer sticky au lieu de fixed, pas de wizard-active sur body */
  embedded?: boolean;
}

const LEFT_OFFSET_BASE = 'tw-left-0 md:tw-left-64';

export const WizardLayout: React.FC<WizardLayoutProps> = ({
  // Stepper props
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  allowNavigation = true,

  // Container
  className = 'tw-bg-white',
  children,

  // Right margin for side panel
  rightMargin = 0,

  // Embedded mode
  embedded = false,

  // Tutorial
  isTutorialActive = false,

  // Footer props (rest)
  ...footerProps
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Lock document scroll while wizard is mounted (standalone only)
  useEffect(() => {
    if (embedded) return;
    document.body.classList.add('wizard-active');
    return () => {
      document.body.classList.remove('wizard-active');
    };
  }, [embedded]);

  // Scroll content area to top on step change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Build right offset dynamically based on panel state (standalone only)
  const rightClass = rightMargin > 0 ? '' : 'tw-right-0';
  const fixedOffset = `${LEFT_OFFSET_BASE} ${rightClass}`;

  // --- Mode embarqué : stepper/footer sticky, hauteur héritée du parent ---
  if (embedded) {
    return (
      <div
        className={`tw-flex tw-flex-col tw-h-full ${className} tw-transition-[margin] tw-duration-300 tw-ease-in-out`}
        style={rightMargin > 0 ? { marginRight: rightMargin } : undefined}
      >
        <WizardStepper
          steps={steps}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={onStepClick}
          allowNavigation={allowNavigation}
          fixedToViewport={false}
          topOffset="tw-top-0"
        />

        <div
          ref={scrollRef}
          className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto wizard-scroll"
        >
          {children}
        </div>

        <WizardFooter
          {...footerProps}
          currentStep={currentStep}
          totalSteps={steps.length}
          fixedToViewport={false}
        />
      </div>
    );
  }

  // --- Mode standalone (comportement d'origine) ---
  return (
    <div
      className={`tw-h-[calc(100vh-4rem)] tw-h-[calc(100dvh-4rem)] tw-overflow-hidden ${className} tw-transition-[margin] tw-duration-300 tw-ease-in-out`}
      style={rightMargin > 0 ? { marginRight: rightMargin } : undefined}
    >
      <WizardStepper
        steps={steps}
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={onStepClick}
        allowNavigation={allowNavigation}
        fixedToViewport
        topOffset="tw-top-16"
        leftOffset={fixedOffset}
        rightOffsetPx={rightMargin > 0 ? rightMargin : undefined}
      />

      {/* pt-28 compense la hauteur du stepper fixe, pb-24 compense le footer fixe */}
      <div
        ref={scrollRef}
        className={`tw-h-[calc(100vh-4rem)] tw-h-[calc(100dvh-4rem)] tw-overflow-y-auto tw-pt-20 sm:tw-pt-28 tw-pb-20 sm:tw-pb-24 wizard-scroll${isTutorialActive ? ' tutorial-layout-active' : ''}`}
      >
        {children}
      </div>

      <WizardFooter
        {...footerProps}
        currentStep={currentStep}
        totalSteps={steps.length}
        fixedToViewport
        leftOffset={fixedOffset}
        rightOffsetPx={rightMargin > 0 ? rightMargin : undefined}
        isTutorialActive={isTutorialActive}
      />
    </div>
  );
};
